import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  FileSpreadsheet,
  HeartPulse,
  Loader2,
  Package,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";
import { logError } from "@/lib/log";

const COLOR_HEX = {
  AMARILLO: "#FACC15",
  NARANJA: "#FB923C",
  ROSA: "#F472B6",
  ROJO: "#EF4444",
  MORADO: "#A855F7",
  AZUL: "#3B82F6",
  VERDE: "#22C55E",
  NEGRO: "#18181B",
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDateEs = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

const ColorDot = ({ color, size = 10 }) => (
  <span
    className="inline-block rounded-full border border-zinc-300 shrink-0"
    style={{
      width: size,
      height: size,
      backgroundColor: COLOR_HEX[color] || "#A1A1AA",
    }}
    aria-hidden
  />
);

export default function MasksManager() {
  const [stock, setStock] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [movements, setMovements] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogType, setDialogType] = useState(null); // "in" | "out" | null
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({
    date: todayIso(),
    instructor_id: undefined,
    note: "",
    qty: {},
  });

  const colors = useMemo(
    () => stock?.available_colors || Object.keys(COLOR_HEX),
    [stock]
  );
  const threshold = stock?.low_stock_threshold ?? 10;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, i, f] = await Promise.all([
        adminApi.get("/admin/masks/stock"),
        adminApi.get("/admin/masks/movements"),
        adminApi.get("/instructors"),
        adminApi.get("/admin/masks/forecast"),
      ]);
      setStock(s.data);
      setMovements(m.data || []);
      setForecast(f.data);
      setInstructors(
        [...(i.data || [])].sort((a, b) =>
          `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, "es")
        )
      );
    } catch (e) {
      logError("No se pudo cargar el control de mascarillas", e);
      toast.error("No se pudo cargar el control de mascarillas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openDialog = (type) => {
    setForm({ date: todayIso(), instructor_id: undefined, note: "", qty: {} });
    setDialogType(type);
  };

  const formTotal = useMemo(
    () =>
      Object.values(form.qty).reduce(
        (acc, v) => acc + (parseInt(v, 10) > 0 ? parseInt(v, 10) : 0),
        0
      ),
    [form.qty]
  );

  const handleSave = async () => {
    const items = colors
      .map((c) => ({ color: c, qty: parseInt(form.qty[c], 10) || 0 }))
      .filter((i) => i.qty > 0);
    if (items.length === 0) {
      toast.error("Indica la cantidad de al menos un color.");
      return;
    }
    if (dialogType === "out" && !form.instructor_id) {
      toast.error("Selecciona el instructor que recibe las mascarillas.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/admin/masks/movements", {
        type: dialogType,
        date: form.date || todayIso(),
        instructor_id: dialogType === "out" ? form.instructor_id : null,
        note: form.note.trim(),
        items,
      });
      toast.success(
        dialogType === "in"
          ? `Entrada registrada: ${formTotal} mascarillas.`
          : `Salida registrada: ${formTotal} mascarillas.`
      );
      setDialogType(null);
      await fetchAll();
    } catch (e) {
      const msg = e.response?.data?.detail || "No se pudo guardar el movimiento.";
      toast.error(typeof msg === "string" ? msg : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/admin/masks/movements/${deleteTarget.id}`);
      toast.success("Movimiento eliminado.");
      setDeleteTarget(null);
      await fetchAll();
    } catch (e) {
      logError("No se pudo eliminar el movimiento", e);
      toast.error("No se pudo eliminar el movimiento.");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/masks/export", {
        responseType: "blob",
      });
      const disposition = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(disposition);
      const filename = (m && m[1]) || `MASCARILLAS_RCP_${todayIso()}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success(`Descargado: ${filename}`);
    } catch (e) {
      logError("Export mascarillas failed", e);
      toast.error("No se pudo descargar el Excel.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="space-y-8" data-testid="masks-manager">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
            <HeartPulse className="h-3.5 w-3.5" />
            Material de regalo
          </div>
          <h2 className="heading-font text-2xl sm:text-3xl font-extrabold text-zinc-950">
            Mascarillas-llavero de RCP
          </h2>
          <p className="text-sm text-zinc-600 max-w-2xl">
            Registra las compras al proveedor y los envíos a cada instructor.
            El stock se calcula por color (entradas − salidas).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => openDialog("in")}
            className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold tracking-wide"
            data-testid="masks-new-in-button"
          >
            <ArrowDownToLine className="h-4 w-4 mr-2" />
            Entrada
          </Button>
          <Button
            onClick={() => openDialog("out")}
            className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white font-bold tracking-wide"
            data-testid="masks-new-out-button"
          >
            <ArrowUpFromLine className="h-4 w-4 mr-2" />
            Salida
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            className="h-11 px-4 border-2 border-zinc-300"
            data-testid="masks-export-button"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            Excel
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-10 text-center text-zinc-500">
          Cargando…
        </div>
      ) : (
        <>
          {/* Stock por color */}
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
            data-testid="masks-stock-grid"
          >
            {(stock?.colors || []).map((c) => {
              // Un color que nunca se ha comprado ni enviado se muestra
              // neutro (no es "sin stock", es que no se trabaja con él).
              const untouched = c.in === 0 && c.out === 0;
              const low = !untouched && c.stock <= threshold;
              return (
                <div
                  key={c.color}
                  className={`rounded-md border-2 bg-white p-4 transition-colors ${
                    untouched
                      ? "border-zinc-200"
                      : c.stock <= 0
                      ? "border-red-300"
                      : low
                      ? "border-amber-300"
                      : "border-zinc-200"
                  }`}
                  data-testid={`masks-stock-${c.color}`}
                >
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
                    <ColorDot color={c.color} />
                    {c.color}
                  </div>
                  <div
                    className={`mt-2 text-3xl font-extrabold font-mono leading-none ${
                      untouched ? "text-zinc-300" : "text-zinc-950"
                    }`}
                  >
                    {c.stock}
                  </div>
                  <div className="mt-2 text-[11px] text-zinc-500 font-mono">
                    +{c.in} / −{c.out}
                  </div>
                  {low && (
                    <div
                      className={`mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide ${
                        c.stock <= 0 ? "text-red-600" : "text-amber-600"
                      }`}
                    >
                      <TriangleAlert className="h-3 w-3" />
                      {c.stock <= 0 ? "Sin stock" : "Stock bajo"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className="rounded-md border-2 border-zinc-900 bg-zinc-950 text-white p-5 flex flex-wrap items-center justify-between gap-4"
            data-testid="masks-totals"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-400">
              <Package className="h-4 w-4" />
              Total en almacén
            </div>
            <div className="flex items-center gap-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                  Entradas
                </div>
                <div className="text-xl font-bold font-mono">
                  {stock?.totals?.in ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                  Salidas
                </div>
                <div className="text-xl font-bold font-mono">
                  {stock?.totals?.out ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                  Stock
                </div>
                <div
                  className="text-3xl font-extrabold font-mono leading-none"
                  data-testid="masks-total-stock"
                >
                  {stock?.totals?.stock ?? 0}
                </div>
              </div>
            </div>
          </div>

          {/* Previsión por instructor */}
          <div className="space-y-3" data-testid="masks-forecast">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h3 className="heading-font text-lg font-bold text-zinc-950">
                  Previsión por instructor
                </h3>
                <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                  Lo que cada instructor tiene en mano (envíos confirmados −
                  mascarillas que ya debería haber entregado, 1 por alumno −
                  las de cortesía que ha apuntado) frente a lo que necesitará
                  en sus próximos cursos, contando los alumnos inscritos o, si
                  no hay Excel, el aforo máximo de cada CCAA.
                </p>
              </div>
              {(forecast?.totals?.missing_30d ?? 0) > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold"
                  data-testid="masks-forecast-alarm"
                >
                  <TriangleAlert className="h-3.5 w-3.5" />
                  Faltan {forecast.totals.missing_30d} para los próximos 30 días
                </span>
              )}
            </div>

            {(forecast?.rows || []).length === 0 ? (
              <div className="bg-white border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center text-sm text-zinc-500">
                Sin envíos ni cursos futuros asignados todavía.
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead>
                    <tr className="bg-zinc-50 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                      <th className="text-left font-semibold px-4 py-3">
                        Instructor
                      </th>
                      <th className="text-right font-semibold px-3 py-3">
                        En mano
                      </th>
                      <th className="text-right font-semibold px-3 py-3">
                        Entregadas
                      </th>
                      <th className="text-right font-semibold px-3 py-3">
                        Cortesía
                      </th>
                      <th className="text-right font-semibold px-3 py-3">
                        Próx. 30 días
                      </th>
                      <th className="text-right font-semibold px-3 py-3">
                        Futuro total
                      </th>
                      <th className="text-left font-semibold px-4 py-3">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecast.rows.map((r) => (
                      <tr
                        key={r.instructor_id}
                        className="border-t border-zinc-100"
                        data-testid={`masks-forecast-row-${r.instructor_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-zinc-950">
                            {r.instructor_name}
                          </div>
                          {r.pending_total > 0 && (
                            <div className="text-[11px] text-amber-700 font-semibold">
                              {r.pending_total} uds pendientes de confirmar
                            </div>
                          )}
                          {r.lost_total > 0 && (
                            <div className="text-[11px] text-zinc-500">
                              {r.lost_total} uds no llegaron
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold text-zinc-950">
                          {r.on_hand}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-zinc-600">
                          {r.delivered_estimate}
                        </td>
                        <td
                          className="px-3 py-3 text-right font-mono text-zinc-600"
                          data-testid={`masks-forecast-courtesy-${r.instructor_id}`}
                        >
                          {r.courtesy_total || 0}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-zinc-700">
                          {r.need_30d}
                          <span className="text-zinc-400 text-[11px]">
                            {" "}
                            ({r.courses_30d})
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-zinc-700">
                          {r.need_future}
                          <span className="text-zinc-400 text-[11px]">
                            {" "}
                            ({r.courses_future})
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {r.status === "alert" ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700">
                              <TriangleAlert className="h-3.5 w-3.5" />
                              Enviar {r.missing_30d} ya
                            </span>
                          ) : r.status === "warning" ? (
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-700">
                              <TriangleAlert className="h-3.5 w-3.5" />
                              Faltan {r.missing_future} a futuro
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-emerald-700">
                              Cubierto
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Movimientos */}
          <div className="space-y-3">
            <h3 className="heading-font text-lg font-bold text-zinc-950">
              Movimientos
              <span className="ml-2 text-sm font-normal text-zinc-500">
                {movements.length}
              </span>
            </h3>
            {movements.length === 0 ? (
              <div
                className="bg-white border-2 border-dashed border-zinc-300 rounded-lg p-10 text-center text-sm text-zinc-500"
                data-testid="masks-empty"
              >
                Todavía no hay movimientos. Empieza con una{" "}
                <span className="font-semibold text-zinc-800">Entrada</span> de
                mascarillas.
              </div>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
                {movements.map((m) => (
                  <div
                    key={m.id}
                    className="px-4 md:px-5 py-3 border-b border-zinc-100 last:border-b-0 flex flex-col md:flex-row md:items-center gap-3"
                    data-testid={`masks-movement-${m.id}`}
                  >
                    <div className="flex items-center gap-3 flex-wrap md:w-72 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${
                          m.type === "in"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-zinc-900 text-white"
                        }`}
                      >
                        {m.type === "in" ? "ENTRADA" : "SALIDA"}
                      </span>
                      <span className="text-sm font-mono text-zinc-700">
                        {formatDateEs(m.date)}
                      </span>
                      <span className="text-sm font-bold font-mono text-zinc-950">
                        {m.total} uds
                      </span>
                      {m.type === "out" && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            m.status === "confirmed"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-100 text-amber-900"
                          }`}
                          data-testid={`masks-status-${m.id}`}
                        >
                          {m.status === "confirmed"
                            ? m.received_total < m.total
                              ? `RECIBIDAS ${m.received_total}`
                              : "RECIBIDO"
                            : "PENDIENTE"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {(m.items || []).map((it) => (
                          <span
                            key={it.color}
                            className="inline-flex items-center gap-1.5 text-xs text-zinc-700"
                          >
                            <ColorDot color={it.color} size={8} />
                            <span className="font-mono font-semibold">
                              {it.qty}
                            </span>
                            {it.color}
                          </span>
                        ))}
                      </div>
                      <div className="mt-1 text-xs text-zinc-500 truncate">
                        {m.instructor_name ? (
                          <span className="font-semibold text-zinc-700">
                            → {m.instructor_name}
                          </span>
                        ) : null}
                        {m.instructor_name && m.note ? " · " : ""}
                        {m.note}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDeleteTarget(m)}
                      className="h-9 px-2 border border-zinc-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700 self-start md:self-auto"
                      data-testid={`masks-delete-${m.id}`}
                      title="Eliminar movimiento"
                      aria-label="Eliminar movimiento"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Diálogo de entrada / salida */}
      <Dialog open={!!dialogType} onOpenChange={(o) => !o && setDialogType(null)}>
        <DialogContent
          className="sm:max-w-2xl max-h-[92vh] overflow-y-auto"
          data-testid="masks-dialog"
        >
          <DialogHeader>
            <DialogTitle className="heading-font text-2xl">
              {dialogType === "in" ? "Entrada de mascarillas" : "Salida a instructor"}
            </DialogTitle>
            <DialogDescription>
              {dialogType === "in"
                ? "Indica cuántas mascarillas entran de cada color."
                : "Elige el instructor e indica cuántas mascarillas de cada color se le envían."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dialogType === "out" && (
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Instructor *
                  </label>
                  <Select
                    value={form.instructor_id || undefined}
                    onValueChange={(v) => setForm({ ...form, instructor_id: v })}
                  >
                    <SelectTrigger
                      className="h-11 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
                      data-testid="masks-instructor-select"
                    >
                      <SelectValue placeholder="Selecciona…" />
                    </SelectTrigger>
                    <SelectContent>
                      {instructors.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.name} {i.surname}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Fecha
                </label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                  data-testid="masks-date-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-end justify-between mb-2">
                <label className="block text-sm font-medium text-zinc-700">
                  Cantidades por color
                </label>
                <span className="text-xs text-zinc-500">
                  Total:{" "}
                  <span
                    className="font-mono font-bold text-zinc-900"
                    data-testid="masks-form-total"
                  >
                    {formTotal}
                  </span>
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {colors.map((c) => (
                  <div key={c}>
                    <label
                      htmlFor={`masks-qty-${c}`}
                      className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-zinc-600 mb-1.5"
                    >
                      <ColorDot color={c} size={9} />
                      {c}
                    </label>
                    <Input
                      id={`masks-qty-${c}`}
                      type="number"
                      min="0"
                      inputMode="numeric"
                      placeholder="0"
                      value={form.qty[c] ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          qty: { ...f.qty, [c]: e.target.value },
                        }))
                      }
                      className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 font-mono"
                      data-testid={`masks-qty-${c}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Nota (opcional)
              </label>
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder={
                  dialogType === "in"
                    ? "Proveedor, nº de pedido…"
                    : "Curso, motivo del envío…"
                }
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="masks-note-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setDialogType(null)}
              className="border border-zinc-300"
              data-testid="masks-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || formTotal === 0}
              className={
                dialogType === "in"
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  : "bg-zinc-950 hover:bg-zinc-800 text-white font-bold"
              }
              data-testid="masks-save-button"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {dialogType === "in" ? "Registrar entrada" : "Registrar salida"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="masks-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el movimiento?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                Se borrará la{" "}
                <span className="font-semibold text-zinc-950">
                  {deleteTarget?.type === "in" ? "entrada" : "salida"}
                </span>{" "}
                del {formatDateEs(deleteTarget?.date)} ({deleteTarget?.total} uds
                {deleteTarget?.instructor_name
                  ? ` · ${deleteTarget.instructor_name}`
                  : ""}
                ) y el stock se recalculará.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="masks-delete-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="masks-delete-confirm"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
